import { useEffect, useState } from "react";
import { toast } from "sonner";
import { getBillingRates, updateBillingRates, getBillingSchoolYears } from "../../lib/api";

const FIELDS = [
  { key: "f_school_year", label: "F — Full (School Year)" },
  { key: "f_summer", label: "F — Full (Summer)" },
  { key: "sss_school_year", label: "SSS — Full + (School Year)" },
  { key: "sss_summer", label: "SSS — Full + (Summer)" },
  { key: "h_school_year", label: "H — Half (School Year)" },
  { key: "h_summer", label: "H — Half (Summer)" },
];

export function BillingRatesTab() {
  const [years, setYears] = useState([]);
  const [year, setYear] = useState("");
  const [rates, setRates] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = (sy) => {
    getBillingRates(sy).then((r) => { setRates(r.data); setYear(r.data.school_year); });
  };

  useEffect(() => {
    getBillingSchoolYears().then((r) => { setYears(r.data.school_years); load(r.data.current); });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { school_year: year };
      FIELDS.forEach((f) => { payload[f.key] = Number(rates[f.key] || 0); });
      const r = await updateBillingRates(payload);
      setRates(r.data);
      toast.success("Billing rates saved.");
    } catch (e) {
      if (!e?.__readonly) toast.error("Could not save rates.");
    } finally {
      setSaving(false);
    }
  };

  if (!rates) return <div className="p-4 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-2xl space-y-4" data-testid="billing-rates-tab">
      <div>
        <h3 className="text-lg font-semibold text-[#00305E]">Billing Rates</h3>
        <p className="text-sm text-muted-foreground">
          Monthly tuition rates per enrollment status. Used across the Billing Dashboard for projected
          charges and daily-rate proration. Half summer is typically billed as a Partial/Daily amount.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium">School Year</label>
        <select
          data-testid="billing-rates-year"
          className="h-9 rounded-md border border-slate-300 px-3 text-sm"
          value={year}
          onChange={(e) => load(e.target.value)}
        >
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-sm font-medium">{f.label}</label>
            <div className="flex items-center gap-1 mt-1">
              <span className="text-slate-500">$</span>
              <input
                data-testid={`rate-${f.key}`}
                type="number"
                step="0.01"
                value={rates[f.key] ?? ""}
                onChange={(e) => setRates({ ...rates, [f.key]: e.target.value })}
                className="h-9 w-full rounded-md border border-slate-300 px-2 text-sm tabular-nums"
              />
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={save}
        disabled={saving}
        data-testid="billing-rates-save"
        className="h-9 rounded-md bg-[#00305E] px-5 text-sm font-semibold text-white disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save Rates"}
      </button>
    </div>
  );
}
